<template>
    <div class="debug-pending-to-make">
        <h3>Debug - PendingToMake</h3>
        
        <div class="debug-section">
            <h4>Productos por Franjas Horarias</h4>
            <div v-for="(productsInSlot, slotKey) in productsBySlot" :key="slotKey" class="debug-item">
                <strong>Franja: {{ getTimeRange(slotKey) }}</strong>
                <div class="slot-info">
                    <span class="slot-time">{{ getTimeRange(slotKey) }}</span>
                    <span>- 🧪</span>
                    <span class="products-count">{{ productsInSlot.length }} productos</span>
                </div>
                <div v-for="product in productsInSlot" :key="product.id" class="product-item">
                    <span class="product-name">{{ product.name }}</span>
                    <span class="product-time">{{ formatProductTime(product.originalTime) }}</span>
                    <span class="product-units">{{ product.currentUnits }} uds</span>
                    <span class="product-time" :class="{'urgent': product.currentTime <= 5, 'overdue': product.currentTime <= 0}">
                        {{ product.currentTime }} min
                    </span>
                </div>
            </div>
        </div>
        
        <div class="debug-section">
            <h4>Información del Sistema</h4>
            <div class="debug-info">
                <p><strong>Hora actual:</strong> {{ currentTime }}</p>
                <p><strong>Total productos:</strong> {{ totalProducts }}</p>
                <p><strong>Productos vencidos:</strong> {{ overdueProductsCount }}</p>
                <p><strong>Productos urgentes (≤5min):</strong> {{ urgentProductsCount }}</p>
                <p><strong>Productos cocinando:</strong> {{ productsCookingCount }}</p>
            </div>
        </div>
        
        <div class="debug-section" v-if="productsCooking.length > 0">
            <h4>Productos Cocinando</h4>
            <div v-for="product in productsCooking" :key="`${product.id}_${product.fryerId}`" class="debug-item cooking">
                <strong>{{ product.name }}</strong>
                <div class="cooking-data">
                    <span>Freidora: {{ product.fryerId }}</span>
                    <span>Unidades: {{ product.currentUnits }}</span>
                    <span>Fin: {{ formatTime(product.estimatedEndTime) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DebugPendingToMake',
    
    props: {
        products: {
            type: Array,
            default: () => []
        },
        currentProducts: {
            type: Array,
            default: () => []
        },
        currentReferenceTime: {
            type: String,
            default: ''
        },
        productsCooking: {
            type: Array,
            default: () => []
        }
    },
    
    computed: {
        currentTime() {
            return moment().format('HH:mm:ss')
        },
        
        totalProducts() {
            return this.products.length
        },
        
        productsCookingCount() {
            return this.productsCooking.length
        },
        
        overdueProductsCount() {
            return this.products.filter(product => product.currentTime <= 0).length
        },
        
        urgentProductsCount() {
            return this.products.filter(product => product.currentTime <= 5 && product.currentTime > 0).length
        },
        
        productsBySlot() {
            const slots = {}
            this.products.forEach(product => {
                const slotKey = product.slotStartTime
                if (!slots[slotKey]) {
                    slots[slotKey] = []
                }
                // Asegurar que el producto tenga originalTime
                const hours = product.hours[Object.keys(product.hours)[0]]
                const productWithOriginalTime = {
                    ...product,
                    originalTime: hours.originalTime
                }
                slots[slotKey].push(productWithOriginalTime)
            })
            return slots
        }
    },

    methods: {
        getTimeRange(slotKey) {
            if (!slotKey) return 'N/A';
            const start = moment(slotKey).format('HH:mm');
            const end = moment(slotKey).add(30, 'minutes').format('HH:mm');
            return `${start} - ${end}`;
        },

        formatTime(time) {
            if (!time) return 'N/A';
            return moment(time).format('HH:mm');
        },

        formatProductTime(time) {
            if (!time) return 'N/A';
            return moment(time).format('HH:mm');
        }
    }
}
</script>

<style lang="scss" scoped>
.debug-pending-to-make {
    font-family: monospace;
    font-size: 12px;
    line-height: 1.4;
    
    h3 {
        color: #e74c3c;
        margin-bottom: 16px;
        border-bottom: 1px solid #ddd;
        padding-bottom: 8px;
    }
    
    .debug-section {
        margin-bottom: 20px;
        
        h4 {
            color: #3498db;
            margin-bottom: 8px;
            font-size: 14px;
        }
    }
    
    .debug-item {
        margin-bottom: 8px;
        padding: 8px;
        background-color: #f8f9fa;
        border-radius: 4px;
        border-left: 3px solid #007bff;
        
        strong {
            color: #2c3e50;
        }
        
        .slot-info {
            margin-top: 4px;
            margin-left: 16px;
            font-size: 11px;
            color: #6c757d;
        }

        .product-item {
            margin-top: 4px;
            margin-left: 16px;
            font-size: 11px;
            color: #28a745;
            background-color: #d4edda;
            padding: 2px 6px;
            border-radius: 3px;

            .product-name {
                font-weight: bold;
            }

            .product-units {
                margin-left: 10px;
            }

            .product-time {
                margin-left: 10px;
                font-size: 10px;
                color: #28a745;
                background-color: #d4edda;
                padding: 2px 6px;
                border-radius: 3px;
                
                &.urgent {
                    background-color: #fff3cd;
                    color: #856404;
                }
                
                &.overdue {
                    background-color: #f8d7da;
                    color: #721c24;
                }
            }
        }
        
        .cooking-data {
            margin-top: 4px;
            margin-left: 16px;
            
            span {
                display: inline-block;
                margin-right: 12px;
                font-size: 11px;
                color: #17a2b8;
                background-color: #d1ecf1;
                padding: 2px 6px;
                border-radius: 3px;
            }
        }
        
        &.cooking {
            border-left-color: #17a2b8;
            background-color: #f0f8ff;
        }
    }
    
    .debug-info {
        p {
            margin-bottom: 4px;
            color: #495057;
            
            strong {
                color: #2c3e50;
            }
        }
    }
}   
</style>

